import { useState, useRef, useEffect, useContext, useMemo } from 'react';
import { ProTable, ColumnsState } from '@ant-design/pro-table';
import type { ActionType } from '@ant-design/pro-table';
import { Button, DatePicker, Input, Tag } from 'antd';
import MomentTimezoneService from '../../services/moment-timezone/MomentTimezoneService';
import moment from 'moment';
import { Moment } from 'moment';
import type { RangeValue } from 'rc-picker/lib/interface';
import './MyProcesses.less';
import { ExportableColumn } from '../../shared/Exporter';
import { ContextApp } from '../../contexts/ContextApp';
import { IStatus } from '../../interfaces/Receipt';
import NewProcessModal from './NewProcessModal';
import ViewWorkflowModal from './ViewWorkflowModal';
import { IProcess, IProcessItem } from '../../interfaces/Workflow';
import { SortOrder } from 'antd/lib/table/interface';
import { useLocation } from 'react-router-dom';
import { EnumsValues } from '../../enums/EnumsValues';
import GraphqlService from '../../services/graphql/GraphqlService';
import ViewMyProcessesModal from './ViewMyProcessesModal';
import { notificationContext } from '../../contexts/NotificationContext';
import { FeatureFlagWrapper } from '../FeatureFlagWrapper/FeatureFlagWrapper';
import { useFlows } from '../../contexts/WorkflowContext';
import useFeatureFlagConfig from '../FeatureFlagWrapper/hooks/useFeatureFlagConfig';

const { RangePicker } = DatePicker;
const { Search } = Input;

const GROUPERS = [
  { id: EnumsValues.WorkflowTabs.Pendientes, name: 'Pendientes' },
  { id: EnumsValues.WorkflowTabs.Vencidos, name: 'Vencidos' },
  { id: EnumsValues.WorkflowTabs.Finalizados, name: 'Finalizados' },
];

const MyProcesses = () => {
  const { customRequest, Query } = GraphqlService();
  const { t, selectedTenantId, user } = useContext(ContextApp);
  const { openNotification } = useContext(notificationContext);
  const { state, dispatch } = useFlows();
  const { processes, activities, loaded } = state;
  const { getDate } = MomentTimezoneService();
  const location = useLocation();

  //Refs
  const actionRef = useRef<ActionType>();
  const processesRef = useRef(processes);
  const activitiesRef = useRef(activities);

  //States
  const [tableKey, setTableKey] = useState<number>(0);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<RangeValue<moment.Moment>>([
    moment().subtract(6, 'days').startOf('day'), // Hace 6 días desde hoy
    moment().endOf('day'), // Hoy (fin del día)
  ]);
  const [selectedGrouper, setSelectedGrouper] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);
  const [sorter, setSorter] =
    useState<{
      field: keyof IProcessItem | undefined;
      order: SortOrder | undefined;
    } | null>(null);
  const [viewProcessModal, setViewProcessModal] = useState<{
    open: boolean;
    data: IProcessItem | null;
  }>({
    open: false,
    data: null,
  });
  const [newProcessModal, setNewProcessModal] = useState<{
    open: boolean;
    data: IProcess[] | null;
  }>({
    open: false,
    data: null,
  });
  const activitiesPath: boolean | undefined =
    location.pathname.split('/')[3] === 'myFlows'
      ? false
      : location.pathname.split('/')[3] === 'activities'
      ? true
      : undefined;
  const [columnsStateMap, setColumnsStateMap] = useState<
    Record<string, ColumnsState>
  >({
    id: {
      show: false,
    },
  });

  const { isAvailable: featureEnabled } = useFeatureFlagConfig({
  featureFlag: EnumsValues.FeatureFlags.workflowPage,
  publicAccess: false,
});

  useEffect(() => {
    const hash = Number(window.location.hash.replace('#', ''));
    if (hash) {
      setSelectedGrouper(hash);
    }
  }, []);

  const filterTableData = (
    dataTable: IProcessItem[],
    searchText: string | undefined,
    dateRange: RangeValue<Moment> | null,
    selectedGrouper: number | undefined,
  ) => {
    return dataTable.filter((item: IProcessItem) => {
      const formattedITS = item.its
        ? moment(item.its).format('DD-MM-YYYY')
        : '';
      const formattedLimitDate = item.limitDate
        ? moment(item.limitDate).format('DD-MM-YYYY')
        : '';

      const matchesText =
        !searchText ||
        [
          item.reason,
          item.process,
          item.activity,
          item.status?.name,
          item.asignTo,
          formattedITS,
          formattedLimitDate,
          item.id?.toString(),
        ].some((field) =>
          field?.toLowerCase().includes(searchText.toLowerCase()),
        );

      const matchesDate =
        !dateRange ||
        (item.its &&
          moment
            .utc(item.its)
            .isBetween(
              moment.utc(dateRange[0]).startOf('day'),
              moment.utc(dateRange[1]).endOf('day'),
              undefined,
              '[]',
            ));

      const matchesGrouper =
        !selectedGrouper || item.status?.id === selectedGrouper;

      return matchesText && matchesDate && matchesGrouper;
    });
  };

  const requestActivities = async (processInstanceId?: string) => {
    try {
      let queryName;
      let queryVariables;
      if (processInstanceId) {
        queryName = Query.getActivitiesHistoryByInstance;
        queryVariables = {
          input: {
            tenantId: String(selectedTenantId),
            processInstanceId: processInstanceId,
            orderBy: 'desc',
          },
        };
      } else {
        queryName = Query.getActivitiesHistory;
        queryVariables = {
          input: {
            tenantId: String(selectedTenantId),
            assignee: String(user!.id),
            orderBy: 'desc',
          },
        };
      }
      const activitiesPromise = await customRequest({
        query: queryName,
        variables: queryVariables,
      }).then((response: any) => {
        const data: IProcessItem[] = [];
        response.forEach((item: any) => {
          data.push({
            id: item.id,
            process: item.flow_name,
            status: {
              id: item.status.id,
              name: item.status.name,
              color:
                item.status.id === EnumsValues.WorkflowTabs.Pendientes
                  ? 'yellow'
                  : item.status.id === EnumsValues.WorkflowTabs.Finalizados
                  ? 'green'
                  : 'red',
            },
            activity: item.name,
            asignTo: item.assignee,
            its: item.start_date,
            limitDate: item.due_date,
            description: item.description,
            comment: item.comment,
            reason: item.reason ? item.reason : '',
            previous_task_comment: item.previous_task_comment,
            previous_task_id: item.previous_task_id,
            assigner: item.assigner,
            assigner_name: item.assigner_name,
            previous_task: item.previous_task,
            flowId: item.flow_id,
            end_date_instance: item.end_date_instance,
          });
        });
        return data;
      });

      const [data] = await Promise.all([activitiesPromise]);
      return {
        data,
        success: true,
        total: 0,
      };
    } catch (error) {
      openNotification({
        type: 'error',
        msj: 'Ocurrió un error al obtener las actividades.',
        context: 'requestActivities',
      });
      return {
        data: [],
        success: false,
        total: 0,
      };
    }
  };

  const requestProcessesData = async () => {
    try {
      // 1. Traer los procesos principales
      const proccessInstancesData = await customRequest({
        query: Query.getFlowsHistory,
        variables: {
          input: {
            tenantId: String(selectedTenantId),
            orderBy: 'desc',
            userId: String(user?.id),
          },
        },
      }).then((response: any) => {
        const data: IProcessItem[] = [];
        response.forEach((item: any) => {
          data.push({
            id: item.id,
            process: item.flow_name,
            status: {
              id: item.flow_instance_status_id,
              name: item.status,
              color:
                item.flow_instance_status_id ===
                EnumsValues.WorkflowTabs.Pendientes
                  ? 'yellow'
                  : item.flow_instance_status_id ===
                    EnumsValues.WorkflowTabs.Finalizados
                  ? 'green'
                  : 'red',
            },
            activity: '', // inicial vacío
            asignTo: '',
            its: item.start_date,
            limitDate: item.due_date,
            description: item.description,
            comment: '',
            reason: item.reason,
            previous_task_comment: '',
            previous_task: '',
            flowId: item.flow_id,
            end_date_instance: item.end_date_instance,
            assigner: '',
            assigner_name: '',
          });
        });

        return data;
      });

      // 2. Esperar a todas las tareas activas y actualizar los ítems
      const ids = proccessInstancesData.map((item) => item.id);

      const lastTasks: any[] = await customRequest({
        query: Query.getLastFlowsActivities,
        variables: {
          input: {
            flowsInstancesIds: ids,
          },
        },
      }).then((response: Array<any>) => response ?? null);
      await Promise.all(
        proccessInstancesData.map(async (item) => {
          const activity = lastTasks.find((act) => act.flow_id === item.id);

          if (activity) {
            item.activity = activity.name;
            item.asignTo = activity.assignee_name;
          }
        }),
      );

      return proccessInstancesData;
    } catch (error) {
      openNotification({
        type: 'error',
        msj: 'Ocurrió un error al obtener los procesos.',
        context: 'requestProcessesData',
      });
    }
  };

  //obtener procesos disponibles para iniciar
  const request = async () => {
    try {
      const processesPromise = await customRequest({
        query: Query.getFlowByTenantId,
        variables: {
          input: {
            tenantId: String(selectedTenantId),
          },
        },
      }).then((response: any) => {
        return response;
      });
      const [data] = await Promise.all([processesPromise]);

      return {
        data,
        success: true,
        total: 0,
      };
    } catch (error) {
      return {
        data: [],
        success: false,
        total: 0,
      };
    }
  };

  const handleGrouperChange = (grouper?: number) => {
    setSelectedGrouper(grouper);
  };

  //recarga la grilla al cambiar filtros
  useEffect(() => {
    actionRef.current?.reload();
  }, [activitiesPath, searchText, dateRange, selectedGrouper]);

  //recarga la grilla al crear un nuevo proceso
  useEffect(() => {
    if (loaded.processes && processes.length > 0) {
      setTableKey((prev) => prev + 1);
    }
  }, [loaded.processes, processes.length]);

  // mantener referencias actualizadas
  useEffect(() => {
    processesRef.current = processes;
    activitiesRef.current = activities;
  }, [processes, activities]);

  const getUpdatedFlows = (
    current: IProcessItem[],
    fetched: IProcessItem[],
  ): IProcessItem[] | null => {
    const fetchedMap = new Map(fetched.map((item) => [item.id, item]));
    let hasChanges = false;

    const updated = current.map((currentFlow) => {
      const updatedFlow = fetchedMap.get(currentFlow.id);

      if (!updatedFlow) return currentFlow;

      const shouldUpdate =
        currentFlow.asignTo !== updatedFlow.asignTo ||
        currentFlow.activity !== updatedFlow.activity ||
        currentFlow.status?.id !== updatedFlow.status?.id;

      if (shouldUpdate) {
        hasChanges = true;
        return {
          ...currentFlow,
          asignTo: updatedFlow.asignTo,
          activity: updatedFlow.activity,
          status: updatedFlow.status,
        };
      }

      return currentFlow;
    });

    return hasChanges ? updated : null;
  };

  const getNewActivities = (
    current: IProcessItem[],
    fetched: IProcessItem[],
  ): IProcessItem[] | null => {
    const currentIds = new Set(current.map((item) => item.id));
    const newItems = [];

    for (const item of fetched) {
      if (!currentIds.has(item.id)) {
        newItems.push(item);
      }
    }

    return newItems.length ? newItems : null;
  };

  const getUpdatedActivities = (
    current: IProcessItem[],
    fetched: IProcessItem[],
  ): IProcessItem[] | null => {
    const updatedItems = [];

    for (const fetchedItem of fetched) {
      const currentItem = current.find((item) => item.id === fetchedItem.id);

      if (currentItem) {
        const hasChanged =
          JSON.stringify(currentItem.comment) !==
          JSON.stringify(fetchedItem.comment);

        if (hasChanged) {
          updatedItems.push(fetchedItem);
        }
      }
    }

    return updatedItems.length ? updatedItems : null;
  };

  useEffect(() => {

    if (!featureEnabled) return; 

    const fetchData = async () => {
      if (activitiesPath) {
        const response: any = await requestActivities();
        const fetched = response?.data ?? [];

        const newItems = getNewActivities(activitiesRef.current, fetched);
        const updatedItems = getUpdatedActivities(
          activitiesRef.current,
          fetched,
        );

        if (newItems || updatedItems) {
          const mergedItems = [...activitiesRef.current];

          if (updatedItems) {
            for (const updatedItem of updatedItems) {
              const index = mergedItems.findIndex(
                (item) => item.id === updatedItem.id,
              );
              if (index !== -1) {
                mergedItems[index] = updatedItem;
              }
            }
          }

          if (newItems) {
            mergedItems.unshift(...newItems);
          }

          dispatch({
            type: 'SET_ACTIVITIES',
            payload: mergedItems,
          });
          actionRef.current?.reload();
        }
      } else {
        const response = await requestProcessesData();
        const fetched = response ?? [];

        const updated = getUpdatedFlows(processesRef.current, fetched);

        if (updated) {
          dispatch({ type: 'SET_PROCESSES', payload: updated });
          actionRef.current?.reload();
        }
      }
    };

    // fetch cada 30 seg si la pestaña está activa
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    }, 30000);

    // fetch al entrar a la ruta
    fetchData();

    return () => {
      clearInterval(interval);
    };
  }, [activitiesPath, featureEnabled]);

  //actualiza en tiempo real el estado de una actividad al completarla
  const updateActivityStatus = (
    activityId: string,
    updatedFields: Partial<IProcessItem>,
  ) => {
    const updated = activities.map((activity) =>
      activity.id === activityId && updatedFields.status
        ? { ...activity, status: updatedFields.status }
        : activity,
    );
    dispatch({ type: 'SET_ACTIVITIES', payload: updated });
    actionRef.current?.reload();
  };

  const sortData = (
    data: IProcessItem[],
    sorter?: { field?: keyof IProcessItem; order?: SortOrder },
  ) => {
    if (!sorter?.field || !sorter?.order) return data;

    return [...data].sort((a, b) => {
      const field = sorter.field as keyof IProcessItem;
      const aValue = a[field];
      const bValue = b[field];

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sorter.order === 'ascend'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (
        (typeof aValue === 'number' && typeof bValue === 'number') ||
        (aValue instanceof Date && bValue instanceof Date)
      ) {
        return sorter.order === 'ascend'
          ? aValue > bValue
            ? 1
            : -1
          : aValue < bValue
          ? 1
          : -1;
      }

      return 0;
    });
  };

  const columns: ExportableColumn<any>[] = useMemo(
    () => [
      {
        export: true,
        dataIndex: 'id',
        title: t('entity.id'),
        sorter: true,
        render: (__: any, record: any) => {
          const [shortId] = record.id.split('-');
          return shortId;
        },
        align: 'left',
        width: '10%',
        hideInForm: true,
        hideInSearch: true,
      },
      {
        export: true,
        dataIndex: 'reason',
        title: t('entity.reason'),
        sorter: true,
        render: (_, record) => {
          return <span>{record.reason ? record.reason : '-'}</span>;
        },
        align: 'left',
        hideInForm: true,
        hideInSearch: false,
        width: '15%',
      },
      {
        export: true,
        dataIndex: 'process',
        title: t('entity.flow'),
        sorter: true,
        render: (_, record) => {
          return <span>{record.process}</span>;
        },
        align: 'left',
        hideInForm: true,
        hideInSearch: false,
        width: '15%',
      },
      {
        export: true,
        dataIndex: 'activity',
        title: activitiesPath ? t('entity.activity') : t('entity.lastActivity'),
        sorter: true,
        render: (_, record) => <span>{record.activity}</span>,
        align: 'left',
        hideInForm: true,
        hideInSearch: false,
        width: '15%',
      },
      {
        export: true,
        dataIndex: 'asignTo',
        title: t('entity.asignTo'),
        sorter: true,
        render: (_, record) => <span>{record.asignTo}</span>,
        align: 'left',
        hideInTable: activitiesPath,
        hideInForm: true,
        hideInSearch: activitiesPath,
        width: '15%',
      },
      {
        export: true,
        dataIndex: 'flowInstance',
        title: t('entity.status'),
        sorter: true,
        renderDataExport: (record: { status: IStatus }) =>
          t(record.status?.translation_key as never, {
            defaultValue: record.status?.name || '-',
          }),
        render: (_, record) => {
          return <Tag color={record.status.color}>{record.status.name}</Tag>;
        },
        align: 'left',
        hideInForm: true,
        hideInSearch: false,
        width: '10%',
      },
      {
        export: true,
        dataIndex: 'its',
        title: t('entity.startdate'),
        // sorter: true,
        render: (_, record) =>
          record.its
            ? getDate({ element: moment(record.its).toDate() }) || '-'
            : '-',
        align: 'left',
        sorter: true,
        hideInForm: true,
        hideInSearch: false,
        width: '10%',
      },
      {
        export: true,
        dataIndex: 'limitDate',
        title: t('entity.limitDate'),
        // sorter: true,
        render: (_, record) => {
          if (!activitiesPath) {
            return record.end_date_instance !== undefined &&
              record.end_date_instance !== null
              ? getDate({ element: moment(record.end_date_instance).toDate() })
              : '-';
          } else {
            return record.limitDate
              ? getDate({ element: moment(record.limitDate).toDate() })
              : '-';
          }
        },
        align: 'left',
        sorter: true,
        hideInForm: true,
        hideInSearch: false,
        width: '10%',
      },
    ],
    [t, activitiesPath],
  );

  const placeholder = useMemo(() => {
    const searchColumns = columns
      .filter((col) => !col.hideInSearch)
      .map((col) => col.title);

    return searchColumns.join(', ');
  }, [columns]);

  return (
    <FeatureFlagWrapper
      featureFlag={EnumsValues.FeatureFlags.workflowPage}
      publicAccess={false}
    >
      <div className="state-tab-container">
        <div
          className={`state-tab ${selectedGrouper ? '' : 'selected'}`}
          onClick={() => handleGrouperChange()}
        >
          {t('entity.all')}
        </div>

        {GROUPERS.map((grouper) => (
          <div
            id={grouper.id.toString()}
            key={grouper.name}
            className={`state-tab ${
              selectedGrouper === grouper.id ? 'selected' : ''
            }`}
            onClick={() => handleGrouperChange(grouper.id)}
          >
            {grouper.name}
          </div>
        ))}
      </div>

      <ProTable<IProcessItem>
        loading={loading}
        actionRef={actionRef}
        key={tableKey}
        columns={columns}
        request={async () => {

          if (!featureEnabled) {
            return { data: [], success: true, total: 0 };
          }
        
          try {
            let data: IProcessItem[] = [];

            if (activitiesPath) {
              setLoading(true);
              if (!loaded.activities) {
                const response = await requestActivities();
                data = response.data ?? [];
                dispatch({ type: 'SET_ACTIVITIES', payload: data });
              } else {
                data = activities;
              }
              setLoading(false);
            } else {
              setLoading(true);
              if (!loaded.processes) {
                const response = await requestProcessesData();
                data = response ?? [];
                dispatch({ type: 'SET_PROCESSES', payload: data });
              } else {
                data = processes;
              }
              setLoading(false);
            }

            const filtered = filterTableData(
              data,
              searchText,
              dateRange,
              selectedGrouper,
            );
            const sorted = sortData(filtered, sorter ?? undefined);

            return {
              data: sorted,
              success: true,
              total: sorted.length,
            };
          } catch (error) {
            return {
              data: [],
              success: false,
              total: 0,
            };
          }
        }}
        rowKey="id"
        search={false}
        options={{ reload: false, density: false }}
        pagination={{ pageSize: 20 }}
        onChange={(_, __, sorter) => {
          if (!Array.isArray(sorter)) {
            setSorter({
              field: sorter.field as keyof IProcessItem,
              order: sorter.order,
            });
          }
        }}
        columnsState={{
          value: columnsStateMap,
          onChange: setColumnsStateMap,
        }}
        onRow={(record) => ({
          style: { cursor: 'pointer' },
          onClick: () => {
            setViewProcessModal({ open: true, data: { ...record } });
          },
        })}
        toolBarRender={() => [
          <RangePicker
            format="DD-MM-YYYY"
            key="dateRange"
            value={dateRange}
            onChange={(range) => setDateRange(range)}
            className="date-picker"
            size="small"
          />,
          <Search
            key="search"
            placeholder={placeholder}
            allowClear
            onSearch={(value) => setSearchText(value)}
            style={{ width: 267, paddingTop: '2px', marginLeft: '8px' }}
            enterButton={
              <Button type="primary">
                <span
                  className="material-symbols-outlined glass-icon"
                  translate="no"
                >
                  search
                </span>
              </Button>
            }
          />,
          <div className="document-table__toolbar-whitespace"></div>,
          !activitiesPath && (
            <Button
              type="primary"
              className="flow-add-btn"
              key="new"
              onClick={async () => {
                const availableProcesses = await request();
                setNewProcessModal({
                  open: true,
                  data: availableProcesses.data,
                });
              }}
            >
              {t('entity.new')}
            </Button>
          ),
        ]}
      />

      {viewProcessModal.open && activitiesPath ? (
        <ViewWorkflowModal
          tenantId={selectedTenantId}
          state={viewProcessModal.open}
          data={viewProcessModal.data}
          onCancel={() => {
            setViewProcessModal({
              open: false,
              data: null,
            });
          }}
          onCompleteActivity={(activityId: string) => {
            updateActivityStatus(activityId, {
              status: {
                id: EnumsValues.WorkflowTabs.Finalizados,
                name: 'Completada',
                color: 'green',
              },
            });
          }}
        />
      ) : (
        <ViewMyProcessesModal
          data={viewProcessModal.data}
          getActivities={(processInstanceId: string) =>
            requestActivities(processInstanceId)
          }
          state={viewProcessModal.open}
          onCancel={() => {
            setViewProcessModal({
              open: false,
              data: null,
            });
          }}
        />
      )}

      {newProcessModal.open && (
        <NewProcessModal
          data={newProcessModal}
          tenantId={selectedTenantId?.toString()}
          user={user?.id.toString()}
          onCancel={() => {
            setNewProcessModal({
              open: false,
              data: null,
            });
          }}
          onSuccess={async () => {
            dispatch({ type: 'SET_LOADED', payload: { processes: false } });
            const newList = await requestProcessesData();
            dispatch({ type: 'SET_PROCESSES', payload: newList ?? [] });
          }}
        />
      )}
    </FeatureFlagWrapper>
  );
};

export default MyProcesses;
